import { createSelector } from 'reselect'
import { getVal, isLoaded } from 'react-redux-firebase'
import { currentBoardIdSelector, currentBlockSelector } from './selectors'
import { firebaseSelector, userIdSelector } from './baseSelectors'
import {
  blockLayoutSelector,
  currentBlockSizeSelector,
  boardSizeSelector,
  colWidthSelector,
  sectionLayoutAdjustedSelector
} from './boardSelectors'

export const newThreadSelector = state => state.discuss.newThread

export const threadsSelector = createSelector(
  [firebaseSelector, userIdSelector, currentBoardIdSelector, newThreadSelector],
  (firebase, userID, boardId, newThread) => {
    let threads = getVal(firebase, `discuss/threads/${boardId}`)
    if (!isLoaded(threads)) return threads
    threads = threads ? { ...threads } : {}
    if (newThread && newThread.id) threads[newThread.id] = newThread
    if (!Object.keys(threads).length) return null
    const viewed = getVal(
      firebase,
      `discuss/viewedThreads/${userID}/${boardId}/`
    )
    const processedThreads = {}
    Object.values(threads).forEach((thread, i) => {
      if (thread.dateDeleted) return
      processedThreads[thread.id] = {
        ...thread,
        read: false,
        number: i + 1
      }

      if (thread.comments && viewed && thread.id in viewed) {
        const lastComment = Object.values(thread.comments).pop()
        if (lastComment.date <= viewed[thread.id].dateViewed)
          processedThreads[thread.id].read = true
      }
    })

    return processedThreads
  }
)

export const showResolvedThreadsSelector = state => state.discuss.showResolved
export const activeThreadSelector = state => state.discuss.activeThread

export const boardThreadsSelector = createSelector(
  [
    threadsSelector,
    showResolvedThreadsSelector,
    sectionLayoutAdjustedSelector,
    blockLayoutSelector,
    activeThreadSelector
  ],
  (threads, showResolved, sections, blocks, activeThread) => {
    if (!isLoaded(threads)) return threads
    const rawThreads = Object.values(threads || [])
    const processedThreads = []
    rawThreads.forEach(thread => {
      if (thread.dateResolved && thread.id !== activeThread && !showResolved)
        return

      if (thread.blockId) {
        const block = blocks[thread.blockId]
        if(!block) return  // FIXME delete comments when block deleted too

        // temp - a few blocks on dev are not stored with relative positions
        if (thread.top > 1) {
          thread.top /= block.height
          thread.left /= block.width
        }

        processedThreads.push({
          ...thread,
          top: thread.top * block.height + block.top,
          left: thread.left * block.width + block.left
        })
      } else {
        const section = sections.get(thread.sectionId)
        if(!section) return // FIXME ensure threads are deleted if section is
        processedThreads.push({
          ...thread,
          top: Math.min(thread.top, section.height) + section.top
        })
      }
    })
    return processedThreads
  }
)

export const blockThreadsSelector = createSelector(
  [
    threadsSelector,
    showResolvedThreadsSelector,
    currentBlockSelector,
    currentBlockSizeSelector,
    boardSizeSelector,
    colWidthSelector
  ],
  (threads, showResolved, block, blockSize, boardSize, colWidth) => {
    if (!isLoaded(threads)) return undefined
    const rawThreads = Object.values(threads || {})
    const processedThreads = []
    // (boardSize.height - 60) / (boardSize.width - 60)
    rawThreads.forEach(thread => {
      if (thread.blockId !== block.id || (thread.dateResolved && !showResolved))
        return
      // thread position relative to center of block
      const position = {
        left: (thread.left - 0.5) * blockSize.width,
        top: (thread.top - 0.5) * blockSize.height
      }
      // thread position relative to board
      position.left = position.left + boardSize.width / 2
      position.top = position.top + boardSize.height / 2

      processedThreads.push({
        ...thread,
        top: position.top / colWidth,
        left: position.left / colWidth
      })
    })
    return processedThreads
  }
)
